import '@mdxeditor/editor/style.css';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  CreateLink,
  linkPlugin,
  linkDialogPlugin,
} from '@mdxeditor/editor';

interface Props {
  value: string;
  onChange: (next: string) => void;
}

/**
 * WYSIWYG markdown editor for GUIDE bodies — ricky-curator's guide editor,
 * ported as-is. Renders in a Word-like surface (headings, bullets, bold/italic,
 * links); output is plain markdown, so what lands in `bodyMarkdown` round-trips
 * cleanly through the publish render.
 *
 * NO `diffSourcePlugin`. Its source mode is a CodeMirror that ships its own
 * light theme and ignores our tokens — it painted a white block inside the dark
 * page. Curator never had the plugin, and the toggle was never wanted: raw
 * markdown editing lives in `MarkdownEditor` (rules), where it's the whole
 * point rather than a hidden second mode.
 *
 * Toolbar is intentionally small: a guide's section structure doesn't need
 * tables, code blocks, or images.
 */
export const RichTextEditor = ({ value, onChange }: Props) => (
  <div className="rounded border border-outline bg-surface-elevated">
    <MDXEditor
      markdown={value}
      onChange={onChange}
      contentEditableClassName="prose prose-sm dark:prose-invert max-w-none px-4 py-3 min-h-96 focus:outline-none"
      plugins={[
        headingsPlugin(),
        listsPlugin(),
        quotePlugin(),
        thematicBreakPlugin(),
        linkPlugin(),
        linkDialogPlugin(),
        markdownShortcutPlugin(),
        toolbarPlugin({
          toolbarContents: () => (
            <>
              <UndoRedo />
              <BlockTypeSelect />
              <BoldItalicUnderlineToggles />
              <ListsToggle />
              <CreateLink />
            </>
          ),
        }),
      ]}
    />
  </div>
);
